export type SharedMailboxDetection =
  | { kind: "shared"; targetMailbox: string }   // auto-detected shared context
  | { kind: "personal" };                       // no auto-detected shared target

/**
 * Canonical source for shared-mailbox target detection (ui/CLAUDE.md rule 1:
 * add-in/ora parity — port, don't fork, if ora ever grows a mailbox surface).
 * NEVER reads Office.context.mailbox.userProfile.emailAddress as the target:
 * on desktop delegate profiles it returns the AM's OWN address (PRD AC-Rule).
 *
 * A shared target is only ever returned when `getSharedPropertiesAsync`
 * (available on classic desktop, where automap works) resolves one. Any
 * surface that cannot auto-detect — the API set is unavailable, the item
 * lacks the call, or detection throws — resolves to `personal`: there is NO
 * manual picker, so an AM launches shared only from a surface where Outlook
 * itself surfaces the shared mailbox. This keeps `target_mailbox` a value the
 * client could only have discovered from the actual Outlook session (the
 * server still re-verifies the AM's access — the client string is never
 * trusted as authorization).
 */
type GetSharedPropertiesAsync = (
  callback: (result: Office.AsyncResult<{ targetMailbox?: string }>) => void,
) => void;

export async function detectTargetMailbox(): Promise<SharedMailboxDetection> {
  try {
    const supported = Office?.context?.requirements?.isSetSupported?.("Mailbox", "1.8") === true;
    if (!supported) return { kind: "personal" };
    const item = Office?.context?.mailbox?.item as
      | { getSharedPropertiesAsync?: GetSharedPropertiesAsync }
      | undefined;
    if (typeof item?.getSharedPropertiesAsync !== "function") return { kind: "personal" };
    const target = await new Promise<string | null>((resolve) => {
      item.getSharedPropertiesAsync!((result: Office.AsyncResult<{ targetMailbox?: string }>) => {
        resolve(result?.status === Office.AsyncResultStatus.Succeeded
          ? result.value?.targetMailbox ?? null : null);
      });
    });
    return target ? { kind: "shared", targetMailbox: target } : { kind: "personal" };
  } catch {
    return { kind: "personal" }; // guarded like silentTriggerFields — never throws
  }
}
